import { useQuery } from '@apollo/client';
import { CircularProgress } from '@material-ui/core';
import { useUserInfo } from '@terragotech/gen5-shared-components';
import { DATETIME_TOKEN_CONVERSION, getDateTimeFormat } from '@terragotech/gen5-shared-utilities';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ROW_HEIGHT } from '../../components/Common/Modals/TGOptionLazyModal/TGOptionModalRowRenderer';
import FluidStyledTableWithData from '../../components/StyledTable/FluidStyledTableWithDataManagement';
import UnauthorizedDisplay from '../../components/UnauthorizedDisplay';
import { Filters, FilterValue } from '../../contexts/AggregatesContext/useAssetsLoader';
import { useAssetsTableState } from '../../contexts/assetsTableStateContext';
import { Column } from '../../hooks/tableHooks/useColumns';
import { TableData } from '../../hooks/useTable';
import { TableDataContext } from '../../hooks/useTableData';
import { useRecoilValue } from 'recoil';
import { configState } from '../../recoil/atoms';
import { GET_SYSTEM_LOGS } from './graphql';
import { useSharedStyles } from '../LogStyles';

interface SystemLog extends TableData {
  id: string;
  type: string;
  timestamp: Date;
  level: string;
  details: string;
}

const HEADER_HEIGHT = 109;
const QUERY = GET_SYSTEM_LOGS;
const NUM_ROWS = 40;

const SystemLogs: React.FC = () => {
  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [logSort, setLogSort] = useState<string[]>(['NATURAL']);
  const [hasMore, setHasMore] = useState(true);
  const [messages, setMessages] = useState<SystemLog[]>([]);
  const [resetComplete, setResetComplete] = useState<boolean>(false);
  const [rowsRendered, setRowsRendered] = useState<number>(NUM_ROWS);
  const { defaultDateTimeFormat } = useRecoilValue(configState);
  const sharedClasses = useSharedStyles();
  const tableHeight = window.innerHeight - HEADER_HEIGHT - 60;
  const loadingMore = useRef(false);
  const { hasSystemLoggingAccess } = useUserInfo();
  const { sortColumn, sortDirection } = useAssetsTableState();
  const { filters, partialFilters, setPartialFilters, setFilters, setFiltersApplied } = useContext(TableDataContext);

  useEffect(() => {
    setPartialFilters({});
    setFilters({});
    setFiltersApplied(false);
    setResetComplete(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: Column<SystemLog>[] = useMemo(
    () => [
      // { key: 'id', name: 'ID', resizable: true, sortable: true, dataType: 'String' },
      { key: 'type', name: 'Type', resizable: true, sortable: true, dataType: 'String' },
      {
        key: 'timestamp',
        name: 'Timestamp',
        dataType: 'DateTime',
        resizable: true,
        sortable: true,
        cellRenderer: ({ row }) => (
          <div>
            {row.timestamp &&
              moment(row.timestamp).format(
                getDateTimeFormat(
                  defaultDateTimeFormat?.dateFormatType,
                  defaultDateTimeFormat?.dateFormat,
                  defaultDateTimeFormat?.dateSeperator,
                  defaultDateTimeFormat?.timeFormat,
                  { tokenConversion: DATETIME_TOKEN_CONVERSION.MomentJS }
                )
              )}
          </div>
        ),
      },
      { key: 'level', name: 'Level', resizable: true, sortable: true, dataType: 'String' },
      { key: 'details', name: 'Details', resizable: true, sortable: true, dataType: 'String', width: 550 },
    ],
    [defaultDateTimeFormat]
  );

  const getCurrFilter = useMemo((): Filters => {
    if (!resetComplete) return { id: { isNull: false } };
    return {
      id: { isNull: false },
      ...Object.fromEntries(
        Object.entries(filters).reduce((acc, [x, y]) => {
          const filterColumn = columns?.find(column => column.key === x);

          if (!filterColumn?.dataType || filterColumn.dataType !== 'DateTime') {
            const value: FilterValue = {
              in: Array.isArray(y) ? y : [y],
            };
            acc.push([x, value]);
          }

          return acc;
        }, [] as [string, FilterValue][])
      ),
      and: Object.entries(filters).reduce((acc, [x, y]) => {
        const filterColumn = columns?.find(column => column.key === x);

        if (filterColumn?.dataType === 'DateTime' && Array.isArray(y) && y.length >= 4) {
          acc.push({ [x]: { greaterThanOrEqualTo: y[1] } });
          acc.push({ [x]: { lessThanOrEqualTo: y[3] } });
        }

        return acc;
      }, [] as Filters[]),
      ...Object.fromEntries(
        Object.entries(partialFilters).reduce((acc, [x, y]) => {
          const filterColumn = columns?.find(column => column.key === x);

          if (filterColumn?.dataType === 'String') {
            acc.push([x, { includesInsensitive: y as string }]);
          }

          return acc;
        }, [] as [string, FilterValue][])
      ),
    };
  }, [filters, partialFilters, columns, resetComplete]);

  const { loading, fetchMore } = useQuery(QUERY, {
    variables: {
      first: NUM_ROWS,
      after: null,
      filter: { id: { isNull: false } },
      orderBy: ['NATURAL'],
    },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const edges = data?.systemLogsConnection?.edges || [];
      const newMessages = edges.map((edge: any) => {
        const { appliedFilters } = edge.node as {
          appliedFilters: JSON;
        };

        return {
          ...edge.node,
          appliedFilters: appliedFilters ? JSON.stringify(appliedFilters) : 'none',
        };
      });

      setMessages(newMessages);
      setAfterCursor(data?.systemLogsConnection?.pageInfo?.endCursor || null);
      setHasMore(data?.systemLogsConnection?.pageInfo?.hasNextPage || false);
    },
    onError: () => {},
  });

  useEffect(() => {
    if (!hasSystemLoggingAccess) return;
    setAfterCursor(null);
    setMessages([]);
    setRowsRendered(NUM_ROWS);
    setHasMore(true);

    let currSort = logSort;
    const sortingColumn = columns?.find(column => column.key === sortColumn);
    if (sortingColumn?.sortable) {
      const normalizedSortColumn = sortColumn?.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase();
      const sortOutput =
        sortDirection === 'NONE' || !sortColumn ? 'NATURAL' : `${normalizedSortColumn}_${sortDirection}`;

      currSort = [sortOutput, 'ID_ASC'];
    }

    fetchMore({
      variables: {
        first: NUM_ROWS,
        after: null,
        filter: getCurrFilter,
        orderBy: currSort,
      },
      updateQuery: (_, { fetchMoreResult }) => {
        const edges = fetchMoreResult?.systemLogsConnection?.edges || [];
        const newMessages = edges.map((edge: any) => ({
          ...edge.node,
          appliedFilters: edge.node.appliedFilters ? JSON.stringify(edge.node.appliedFilters) : '',
        }));

        setMessages(newMessages);
        setAfterCursor(fetchMoreResult?.systemLogsConnection?.pageInfo?.endCursor || null);
        setHasMore(fetchMoreResult?.systemLogsConnection?.pageInfo?.hasNextPage || false);
        if (currSort !== logSort) {
          setLogSort(currSort);
        }
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, partialFilters, sortColumn, sortDirection]);

  const loadMoreData = useCallback(
    async (input: number) => {
      if (!hasMore || loading || loadingMore.current || !hasSystemLoggingAccess) return;
      if (!(input + Math.floor(tableHeight / ROW_HEIGHT) >= rowsRendered - 5)) return;

      loadingMore.current = true;
      await fetchMore({
        variables: {
          first: NUM_ROWS,
          after: afterCursor,
          filter: getCurrFilter,
          orderBy: logSort,
        },
        updateQuery: (_, { fetchMoreResult }) => {
          const edges = fetchMoreResult?.systemLogsConnection?.edges || [];
          const newMessages = edges.map((edge: any) => ({
            ...edge.node,
            appliedFilters: edge.node.appliedFilters ? JSON.stringify(edge.node.appliedFilters) : '',
          }));

          setMessages(prev => [...prev, ...newMessages]);
          setAfterCursor(fetchMoreResult?.systemLogsConnection?.pageInfo?.endCursor || null);
          setHasMore(fetchMoreResult?.systemLogsConnection?.pageInfo?.hasNextPage || false);
          setRowsRendered(prevRows => prevRows + NUM_ROWS);
          loadingMore.current = false;
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasMore, fetchMore, loading, rowsRendered, afterCursor]
  );

  return (
    <>
      {hasSystemLoggingAccess ? (
        <div className={sharedClasses.root}>
          {loading && !messages.length ? (
            <div className={sharedClasses.loader}>
              <CircularProgress />
            </div>
          ) : (
            <>
              <FluidStyledTableWithData<SystemLog>
                columns={columns}
                data={messages}
                searchBar={() => <></>}
                searchBarHeight={0}
                onLoad={loadMoreData}
                height={tableHeight}
                disableSort
                schema={'log'}
                tableName="system_log"
              />
            </>
          )}
        </div>
      ) : (
        <UnauthorizedDisplay />
      )}
    </>
  );
};

export default SystemLogs;
